<template>
    <div>
        <div v-if="formSection==`Drug`">
            <card title="Brand Record">
                <form>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.ndc"
                                      :disabled="disableFields"
                                      :required="true"
                                      label="NDC"
                                      type="string"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.name"
                                      :disabled="disableFields"
                                      label="Name"
                                      required
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.labelName"
                                      :disabled="disableFields"
                                      label="Label Name">
                                <template #label>
                                    Label Name
                                    <small class="text-muted">(Used for printing labels and documents. Falls back to Name if not specified.)</small>
                                </template>
                            </fg-input>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col cols="3">
                            <fg-input v-model.number="drug.awp"
                                      :disabled="disableFields"
                                      :no-wheel="true"
                                      label="AWP"
                                      placeholder="$0.00"
                                      type="number"
                                      @input="onAwpManualChange">
                                <template #label>
                                    AWP
                                    <small v-if="!drug.awpManualOverride && drug.awp" class="text-muted">(auto-calculated)</small>
                                </template>
                            </fg-input>
                        </b-col>
                        <b-col cols="3">
                            <fg-input v-model.number="drug.packageSize"
                                      :disabled="disableFields"
                                      :no-wheel="true"
                                      label="Package Size"
                                      type="number"
                                      required>
                            </fg-input>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.unitOfMeasure"
                                      :disabled="disableFields"
                                      label="Unit of Measure"
                                      required
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col cols="5">
                            <fg-input v-model.number="drug.minimumDispenseQuantity"
                                      :disabled="disableFields"
                                      :no-wheel="true"
                                      label="Minimum Dispense Quantity"
                                      required
                                      type="number">
                            </fg-input>
                        </b-col>
                        <b-col cols="3">
                            <fg-input v-model.number="drug.deaClass"
                                      :disabled="disableFields"
                                      label="DEA Class"
                                      type="number"/>
                        </b-col>
                        <b-col cols="4">
                            <fg-input v-model.number="drug.auditChance"
                                      :disabled="disableFields"
                                      :no-wheel="true"
                                      label="Audit Chance (%)"
                                      type="number"
                                      min="0"
                                      max="100"
                                      placeholder="0-100"
                                      @change="clampAuditChance">
                            </fg-input>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <b-checkbox v-model="drug.enforceMinimumDispenseMultiples" :disabled="disableFields" inline>
                                Enforce Billing in Even Multiples
                            </b-checkbox>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.instructions"
                                      :disabled="disableFields"
                                      label="Instructions"

                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <EnumSelector v-model="drug.dosageForm"
                                          :disabled="disableFields"
                                          :enum-codes="dosageForms"
                                          label="Dosage Form">
                                <template #label>
                                    Dosage Form
                                    <small class="text-muted">(Used for NCPDP billing claims)</small>
                                </template>
                            </EnumSelector>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.routeOfAdministration"
                                      :disabled="disableFields"
                                      label="Route of Administration">
                                <template #label>
                                    Route of Administration
                                    <small class="text-muted">(NCPDP E2 field, max 11 characters)</small>
                                </template>
                            </fg-input>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <EnumSelector v-model="drug.compoundType"
                                          :disabled="disableFields"
                                          :enum-codes="compoundTypes"
                                          label="Compound Type">
                                <template #label>
                                    Compound Type
                                    <small class="text-muted">(NCPDP G1 field - sent based on PBM configuration)</small>
                                </template>
                            </EnumSelector>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <EnumSelector v-model="drug.levelOfEffort"
                                          :clearable="true"
                                          :disabled="disableFields"
                                          :enum-codes="levelOfEffortCodes"
                                          :display-names="levelOfEffortDisplayNames"
                                          label="Level of Effort">
                                <template #label>
                                    Level of Effort
                                    <small class="text-muted">(NCPDP 474-8E field - auto-applied to billing)</small>
                                </template>
                            </EnumSelector>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <EnumSelector v-model="drug.status"
                                          :disabled="disableFields"
                                          :enum-codes="statuses"
                                          label="Status"
                                          required></EnumSelector>
                        </b-col>
                    </b-form-row>
                    <hr>
                </form>
                <b-col cols="12">
                    <b-button variant="success" @click="continueClicked">Continue</b-button>
                    <b-button @click="backToCompoundDashboard">Cancel</b-button>
                </b-col>
            </card>
        </div>

        <div v-if="formSection==`Worksheet`">
            <card title="Formula Worksheet Details">
                <form>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.physicalDescription"
                                      :disabled="disableFields">
                                <template #label>
                                    Description
                                    <small class="text-muted">(Appearance of the finished product)</small>
                                </template>
                            </fg-input>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <b-form-group label="Equipment">
                                <b-form-textarea v-model="drug.equipment"
                                                 :disabled="disableFields"
                                                 rows="2" max-rows="4" />
                            </b-form-group>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.packaging" :disabled="disableFields" label="Packaging" />
                        </b-col>
                        <b-col>
                            <fg-input v-model="drug.labeling" :disabled="disableFields" label="Labeling" />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.flavor" :disabled="disableFields" label="Flavor" />
                        </b-col>
                        <b-col>
                            <fg-input v-model="drug.formulaClass" :disabled="disableFields" label="Class" />
                        </b-col>
                        <b-col>
                            <fg-input v-model="drug.pccaId" :disabled="disableFields" label="PCCA ID" />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.dispensingPackSize"
                                      :disabled="disableFields"
                                      label="Dispensing Pack Size" />
                        </b-col>
                        <b-col>
                            <fg-input v-model.number="drug.daysToExpiration"
                                      :disabled="disableFields"
                                      type="number"
                                      label="Days to Expiration" />
                        </b-col>
                        <b-col>
                            <fg-input v-model.number="drug.timeToMakeMinutes"
                                      :disabled="disableFields"
                                      type="number"
                                      label="Time to Make (minutes)" />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <b-form-group>
                                <template #label>
                                    Directions Preamble
                                    <small class="text-muted">
                                        (Unnumbered lines printed above step 1 -- not a preparation step)
                                    </small>
                                </template>
                                <b-form-textarea v-model="drug.directionsPreamble"
                                                 :disabled="disableFields"
                                                 rows="3" max-rows="6" />
                            </b-form-group>
                        </b-col>
                    </b-form-row>
                    <hr>
                </form>
                <b-col cols="12">
                    <b-button variant="success" @click="continueClicked">Continue</b-button>
                    <b-button @click="backToCompoundDashboard">Cancel</b-button>
                </b-col>
            </card>
        </div>

        <div v-if="formSection==`PatEd`">
            <card title="Patient Education Record">
                <form>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.description"
                                      :disabled="disableFields"
                                      label="Description"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.contraindications"
                                      :disabled="disableFields"
                                      label="Contraindications"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.administration"
                                      :disabled="disableFields"
                                      label="Administration"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.missedDoseInstructions"
                                      :disabled="disableFields"
                                      label="Missed Dose Instructions"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.monitoring"
                                      :disabled="disableFields"
                                      label="Monitoring"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.sideEffects"
                                      :disabled="disableFields"
                                      label="Side Effects"
                            />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <fg-input v-model="drug.patEd.storage"
                                      :disabled="disableFields"
                                      label="Storage"
                            />
                        </b-col>
                    </b-form-row>
                </form>
                <b-col cols="12">
                    <b-button variant="success" @click="continueClicked">Continue</b-button>
                    <b-button @click="backToCompoundDashboard">Cancel</b-button>
                </b-col>
            </card>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Mixins, Prop } from 'vue-property-decorator';
    import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';
    import { CompoundDrug, CompoundDosageForm, CompoundType, CompoundLevelOfEffort, CompoundLevelOfEffortDisplayNames } from '@/models/Drug/CompoundDrug';
    import EnumSelector from '@/components/EnumSelector.vue';
    import Checkbox from "@/components/Inputs/Checkbox.vue";
    import { DrugStatus } from "@/models/Drug/Drug";

    @Component({
        name: "CompoundFormComponent",
        components: {
            EnumSelector,
            Checkbox
        }
    })
    export default class CompoundFormComponent extends Mixins(DisableAutocompleteMixin) {
        @Prop() private drug!: CompoundDrug;
        @Prop() private formSection!: string;
        private statuses = DrugStatus.DrugStatusEnum;
        private dosageForms = CompoundDosageForm;
        private compoundTypes = CompoundType;
        private levelOfEffortCodes = CompoundLevelOfEffort;
        private levelOfEffortDisplayNames = CompoundLevelOfEffortDisplayNames;


        get disableFields(): boolean {
            return false
        }

        created() {
            //console.log("%c Created NonGSDDFormComponent", "color: green");
        }

        onAwpManualChange(value: number | null) {
            // If user clears the field, reset to auto-calculate mode
            this.drug.awpManualOverride = !!value;
        }

        clampAuditChance() {
            if (this.drug.auditChance === null || this.drug.auditChance === undefined || isNaN(this.drug.auditChance as any)) {
                this.drug.auditChance = 0;
                return;
            }
            this.drug.auditChance = Math.max(0, Math.min(100, this.drug.auditChance));
        }

        continueClicked() {
            if (this.drug.instructions == null) this.drug.instructions = "";
            if (this.drug.ndc == null || this.drug.name == null || this.drug.packageSize == null || this.drug.unitOfMeasure == null ||
                this.drug.minimumDispenseQuantity == null ||
                this.drug.ndc.trim() == "" || this.drug.name.trim() == "" || this.drug.unitOfMeasure.trim() == "" ||
                this.drug.minimumDispenseQuantity < 0.0001) {
                this.$bvModal.msgBoxOk(`Required Fields: NDC, Name, Package Size ,Unit of Measure, Minimum Dispense Quantity - can not be left empty`);
                return
            }

            this.clampAuditChance();

            this.$emit('continue');
        }

        backToCompoundDashboard() {
            this.$emit("close");
            return;
        }
    }
</script>

<style scoped>
</style>
